import { getTemplateComponent } from "./TemplateComponentRegistry";

/**
 * 下架模板清單（集中管理）
 * 你只要改這裡，就會連動：模板列表 / 詳情頁 / side_ad / SliderData
 * @type {number[]}
 */
const OFFLINE_TEMPLATE_NUMBERS = [2, 3, 9, 12, 15, 16, 17, 18, 22, 23, 26, 27];
const OFFLINE_TEMPLATE_LINKS = new Set(
  OFFLINE_TEMPLATE_NUMBERS.map((n) => `E${String(n).padStart(2, "0")}`),
);

/**
 * 判斷模板是否上架
 * - 若資料本身有 `template_is_active`，以它為準（可手動 override）
 * - 否則以 OFFLINE_TEMPLATE_LINKS 推導
 * @param {any} item
 * @returns {boolean}
 */
const isTemplateActive = (item) => {
  const flag = item?.template_is_active;
  if (typeof flag === "boolean") return flag;
  return !OFFLINE_TEMPLATE_LINKS.has(item?.template_link);
};

/**
 * 將 side_ad 值正規化成兩位數字字串（例：5 -> '05'、'05' -> '05'、'E5' -> '05'）
 * @param {string|number} value
 * @returns {string|null}
 */
const normalizeSideAdValue = (value) => {
  const raw = String(value ?? "").trim();
  const match = raw.match(/^E(\d+)$/i);
  const digits = match ? match[1] : raw;
  if (!/^\d+$/.test(digits)) return null;
  return digits.padStart(2, "0");
};

/**
 * 開發期防呆：檢查 side_ad / 資料引用是否指向不存在或下架模板
 * @param {any[]} activeItems - 已上架的模板列表
 */
const validateTemplateReferences = (activeItems) => {
  if (process.env.NODE_ENV === "production") return;

  const activeLinks = new Set(activeItems.map((item) => item.template_link));
  const problems = [];

  activeItems.forEach((item) => {
    const sideAds = Array.isArray(item.template_side_ad)
      ? item.template_side_ad
      : [];
    sideAds.forEach((v) => {
      const normalized = normalizeSideAdValue(v);
      if (!normalized) {
        problems.push(
          `[TemplateItemsData] ${item.template_link} side_ad 值不合法：${String(
            v,
          )}`,
        );
        return;
      }
      const link = `E${normalized}`;
      if (!activeLinks.has(link)) {
        problems.push(
          `[TemplateItemsData] ${item.template_link} side_ad 指向不存在/已下架：${link}`,
        );
      }
    });
  });

  if (problems.length) {
    // 一次性列出，避免 console 被洗版
    console.warn(problems.join("\n"));
  }
};

/**
 * 自動化 hot/new 標籤（只看「未下架且會輸出」的模板）
 * 規則：列表 reverse 後前 3 筆 = new；第 4~5 筆 = hot；其餘 = ""
 * 順序依 BaseTemplateItemsData 陣列位置（越後面 = 列表越上面）
 * @param {any[]} activeItems
 * @returns {Map<string, string>} key=template_link, value=template_tag
 */
const buildAutoTagMap = (activeItems) => {
  const tagMap = new Map();
  const displayOrder = [...activeItems].reverse();

  displayOrder.forEach((item, index) => {
    let tag = "";
    if (index <= 2) tag = "new";
    else if (index <= 4) tag = "hot";
    tagMap.set(item.template_link, tag);
  });

  return tagMap;
};

// 自動為每個模板項目添加對應的 Tab2 組件
const addTemplateComponents = (templateData) => {
  // 先套用下架邏輯（只輸出上架的模板）
  const activeItems = templateData
    .map((item) => ({
      ...item,
      // 沒填就自動補上（可在 item 內手動設定 true/false 覆蓋）
      template_is_active: isTemplateActive(item),
    }))
    .filter((item) => item.template_is_active !== false);

  const activeLinks = new Set(activeItems.map((item) => item.template_link));
  const autoTagMap = buildAutoTagMap(activeItems);

  // 清理 side_ad：只保留仍上架且存在的模板
  const computed = activeItems.map((item) => {
    const originalSideAds = Array.isArray(item.template_side_ad)
      ? item.template_side_ad
      : [];
    const seen = new Set();
    const cleanedSideAds = originalSideAds
      .map(normalizeSideAdValue)
      .filter((v) => v && activeLinks.has(`E${v}`))
      .filter((v) => {
        if (seen.has(v)) return false;
        seen.add(v);
        return true;
      });

    return {
      ...item,
      template_side_ad: cleanedSideAds,
      // ✅ 自動化 hot/new：允許你用 template_tag_override 手動覆蓋（避免少數特殊活動例外）
      // - template_tag_override: "new" | "hot" | "" | undefined
      template_tag:
        typeof item.template_tag_override === "string"
          ? item.template_tag_override
          : (autoTagMap.get(item.template_link) ?? ""),
      template_tab2: getTemplateComponent(item.template_link),
    };
  });

  validateTemplateReferences(computed);
  return computed;
};

// 基礎模板資料（不包含 template_tab2，會自動添加）
const BaseTemplateItemsData = [
  // template_category:目前有6個分類 "节庆模版 festival","趣味模版 joy ","体育模版 sports ","视讯模版 live", "捕鱼模版 fishing", "电子模版 slot"
  // 特例：允許覆蓋自動標籤，template_tag_override: new | hot | "" | undefined

  {
    template_id: 2,
    template_category: "festival",
    template_category_name: "节庆模版",
    template_title: "迎接双12",
    template_intro: "迎接双12 百万狂赠到底",
    template_link: "E02",
    template_download_event_title: "迎接双12 - 活动图包.zip",
    template_download_event_link:
      "https://bc9qk72ce.com/archives/迎接双12 - 活动图包.zip",
    template_download_template_title: "迎接双12-模版图包.zip",
    template_download_template_link:
      "https://bc9qk72ce.com/archives/迎接双12-模版图包.zip",
    template_download_promotion_title: "迎接双12 - 广宣素材.zip",
    template_download_promotion_link:
      "https://bc9qk72ce.com/archives/迎接双12 - 广宣素材.zip",
    template_side_ad: ["05", "03", "02", "06"],
  },
  {
    template_id: 3,
    template_category: "festival",
    template_category_name: "节庆模版",
    template_title: "聖誕小玛莉",
    template_intro: "圣诞转好礼 暖冬大礼遇",
    template_link: "E03",
    template_download_event_title: "圣诞转好礼 - 活动图包.zip",
    template_download_event_link:
      "https://bc9qk72ce.com/archives/圣诞转好礼 - 活动图包.zip",
    template_download_template_title: "圣诞转好礼 - 模版图包.zip",
    template_download_template_link:
      "https://bc9qk72ce.com/archives/圣诞转好礼 - 模版图包.zip",
    template_download_promotion_title: "圣诞转好礼 - 广宣素材.zip",
    template_download_promotion_link:
      "https://bc9qk72ce.com/archives/圣诞转好礼 - 广宣素材.zip",
    template_side_ad: ["05", "03", "02", "06"],
  },
  {
    template_id: 4,
    template_category: "festival",
    template_category_name: "节庆模版",
    template_title: "迎春小玛莉",
    template_intro: "千万金喜迎新年 迎春致富大发财",
    template_link: "E04",
    template_download_event_title: "迎春小玛莉 -活动图包.zip",
    template_download_event_link:
      "https://bc9qk72ce.com/archives/迎春小玛莉 -活动图包.zip",
    template_download_template_title: "迎春小玛莉 - 模版图包.zip",
    template_download_template_link:
      "https://bc9qk72ce.com/archives/迎春小玛莉 - 模版图包.zip",
    template_download_promotion_title: "迎春小玛莉 - 广宣素材.zip",
    template_download_promotion_link:
      "https://bc9qk72ce.com/archives/迎春小玛莉 - 广宣素材.zip",
    template_side_ad: ["05", "03", "02", "06"],
  },
  {
    template_id: 5,
    template_category: "festival",
    template_category_name: "节庆模版",
    template_title: "新春刮刮乐",
    template_intro: "新春刮刮乐 刮出发财金",
    template_link: "E05",
    template_download_event_title: "新春刮刮乐 - 活动图包.zip",
    template_download_event_link:
      "https://bc9qk72ce.com/archives/新春刮刮乐 - 活动图包.zip",
    template_download_template_title: "新春刮刮乐 - 活动页全图包.zip",
    template_download_template_link:
      "https://bc9qk72ce.com/archives/新春刮刮乐 - 活动页全图包.zip",
    template_download_promotion_title: "新春刮刮乐 - 广宣素材.zip",
    template_download_promotion_link:
      "https://bc9qk72ce.com/archives/新春刮刮乐 - 广宣素材.zip",
    template_side_ad: ["05", "03", "02", "06"],
  },
  {
    template_id: 6,
    template_category: "festival",
    template_category_name: "节庆模版",
    template_title: "爱神刮刮乐",
    template_intro: "爱神刮刮乐 一手刮大礼",
    template_link: "E06",
    template_download_event_title: "爱神刮刮乐 - 活动图包.zip",
    template_download_event_link:
      "https://bc9qk72ce.com/archives/爱神刮刮乐 - 活动图包.zip",
    template_download_template_title: "爱神刮刮乐 - 活动页全图包.zip",
    template_download_template_link:
      "https://bc9qk72ce.com/archives/爱神刮刮乐 - 活动页全图包.zip",
    template_download_promotion_title: "爱神刮刮乐 - 广宣素材.zip",
    template_download_promotion_link:
      "https://bc9qk72ce.com/archives/爱神刮刮乐 - 广宣素材.zip",
    template_side_ad: ["05", "03", "02", "06"],
  },
  {
    template_id: 7,
    template_category: "festival",
    template_category_name: "节庆模版",
    template_title: "元宵刮刮乐",
    template_intro: "元宵刮刮乐 一刮好运来",
    template_link: "E07",
    template_download_event_title: "元宵刮刮乐 - 活动图包.zip",
    template_download_event_link:
      "https://bc9qk72ce.com/archives/元宵刮刮乐 - 活动图包.zip",
    template_download_template_title: "元宵刮刮乐 - 活动页全图包.zip",
    template_download_template_link:
      "https://bc9qk72ce.com/archives/元宵刮刮乐 - 活动页全图包.zip",
    template_download_promotion_title: "元宵刮刮乐 - 广宣素材.zip",
    template_download_promotion_link:
      "https://bc9qk72ce.com/archives/元宵刮刮乐 - 广宣素材.zip",
    template_side_ad: ["05", "03", "02", "06"],
  },
  {
    template_id: 8,
    template_category: "joy",
    template_category_name: "趣味模版",
    template_title: "礼物树",
    template_intro: "钜惠感谢祭 感恩相伴 感谢有礼",
    template_link: "E08",
    template_download_event_title: "礼物树 - 活动图包.zip",
    template_download_event_link:
      "https://bc9qk72ce.com/archives/礼物树 - 活动图包.zip",
    template_download_template_title: "礼物树 - 活动页全图包.zip",
    template_download_template_link:
      "https://bc9qk72ce.com/archives/礼物树 - 活动页全图包.zip",
    template_download_promotion_title: "礼物树 - 广宣素材.zip",
    template_download_promotion_link:
      "https://bc9qk72ce.com/archives/礼物树 - 广宣素材.zip",
    template_side_ad: ["05", "03", "02", "06"],
  },
  {
    template_id: 9,
    template_category: "joy",
    template_category_name: "趣味模版",
    template_title: "百万糖果树",
    template_intro: "百万糖果树 玩糖果 享回馈",
    template_link: "E09",
    template_download_event_title: "百万糖果树 - 活动图包.zip",
    template_download_event_link:
      "https://bc9qk72ce.com/archives/百万糖果树 - 活动图包.zip",
    template_download_template_title: "百万糖果树 - 活动页全图包.zip",
    template_download_template_link:
      "https://bc9qk72ce.com/archives/百万糖果树 - 活动页全图包.zip",
    template_download_promotion_title: "百万糖果树 - 广宣素材.zip",
    template_download_promotion_link:
      "https://bc9qk72ce.com/archives/百万糖果树 - 广宣素材.zip",
    template_side_ad: ["05", "03", "02", "06"],
  },
  {
    template_id: 10,
    template_category: "joy",
    template_category_name: "趣味模版",
    template_title: "百万摇钱树",
    template_intro: "百万摇钱树 天天金喜得财富",
    template_link: "E10",
    template_download_event_title: "百万摇钱树 - 活动图包.zip",
    template_download_event_link:
      "https://bc9qk72ce.com/archives/百万摇钱树 - 活动图包.zip",
    template_download_template_title: "百万摇钱树 - 活动页全图包.zip",
    template_download_template_link:
      "https://bc9qk72ce.com/archives/百万摇钱树 - 活动页全图包.zip",
    template_download_promotion_title: "百万摇钱树 - 广宣素材.zip",
    template_download_promotion_link:
      "https://bc9qk72ce.com/archives/百万摇钱树 - 广宣素材.zip",
    template_side_ad: ["05", "03", "02", "06"],
  },
  {
    template_id: 11,
    template_category: "joy",
    template_category_name: "趣味模版",
    template_title: "百万幸运转轮",
    template_intro: "玩转手气 好运连连",
    template_link: "E11",
    template_download_event_title: "幸运轮盘 - 活动图包.zip",
    template_download_event_link:
      "https://bc9qk72ce.com/archives/幸运轮盘 - 活动图包.zip",
    template_download_template_title: "幸运轮盘 - 活动页全图包.zip",
    template_download_template_link:
      "https://bc9qk72ce.com/archives/幸运轮盘 - 活动页全图包.zip",
    template_download_promotion_title: "幸运轮盘 - 广宣素材.zip",
    template_download_promotion_link:
      "https://bc9qk72ce.com/archives/幸运轮盘 - 广宣素材.zip",
    template_side_ad: ["05", "03", "02", "06"],
  },
  {
    template_id: 12,
    template_category: "joy",
    template_category_name: "趣味模版",
    template_title: "海盜大秘宝",
    template_intro: "百万奖金抽不完",
    template_link: "E12",
    template_download_event_title: "海盗轮盘 - 活动图包.zip",
    template_download_event_link:
      "https://bc9qk72ce.com/archives/海盗轮盘 - 活动图包.zip",
    template_download_template_title: "海盗轮盘 - 活动页全图包.zip",
    template_download_template_link:
      "https://bc9qk72ce.com/archives/海盗轮盘 - 活动页全图包.zip",
    template_download_promotion_title: "海盗轮盘 - 广宣素材.zip",
    template_download_promotion_link:
      "https://bc9qk72ce.com/archives/海盗轮盘 - 广宣素材.zip",
    template_side_ad: ["05", "03", "02", "06"],
  },
  {
    template_id: 13,
    template_category: "joy",
    template_category_name: "趣味模版",
    template_title: "白金幸运转轮",
    template_intro: "免费拼手气 转出百万奖励",
    template_link: "E13",
    template_download_event_title: "白金轮盘 - 活动图包.zip",
    template_download_event_link:
      "https://bc9qk72ce.com/archives/白金轮盘 - 活动图包.zip",
    template_download_template_title: "白金轮盘 - 活动页全图包.zip",
    template_download_template_link:
      "https://bc9qk72ce.com/archives/白金轮盘 - 活动页全图包.zip",
    template_download_promotion_title: "白金轮盘 - 广宣素材.zip",
    template_download_promotion_link:
      "https://bc9qk72ce.com/archives/白金轮盘 - 广宣素材.zip",
    template_side_ad: ["05", "03", "02", "06"],
  },
  {
    template_id: 14,
    template_category: "festival",
    template_category_name: "节庆模版",
    template_title: "5.1 劳动节",
    template_intro: "回馈耕耘高豐收",
    template_link: "E14",
    template_download_event_title: "5.1劳动节 - 活动图包.zip",
    template_download_event_link:
      "https://bc9qk72ce.com/archives/5.1劳动节 - 活动图包.zip",
    template_download_template_title: "5.1劳动节 - 活动页全图包.zip",
    template_download_template_link:
      "https://bc9qk72ce.com/archives/5.1劳动节 - 活动页全图包.zip",
    template_download_promotion_title: "5.1劳动节 - 广宣素材.zip",
    template_download_promotion_link:
      "https://bc9qk72ce.com/archives/5.1劳动节 - 广宣素材.zip",
    template_side_ad: ["15", "16", "13", "09"],
  },
  {
    template_id: 15,
    template_category: "joy",
    template_category_name: "趣味模版",
    template_title: "百万黄金传说",
    template_intro: "勇闯宝藏之境",
    template_link: "E15",
    template_download_event_title: "百万传说 - 活动图包.zip",
    template_download_event_link:
      "https://bc9qk72ce.com/archives/百万传说 - 活动图包.zip",
    template_download_template_title: "百万传说 - 活动页全图包.zip",
    template_download_template_link:
      "https://bc9qk72ce.com/archives/百万传说 - 活动页全图包.zip",
    template_download_promotion_title: "百万传说 - 广宣素材.zip",
    template_download_promotion_link:
      "https://bc9qk72ce.com/archives/百万传说 - 广宣素材.zip",
    template_side_ad: ["14", "10", "01", "12"],
  },
  {
    template_id: 16,
    template_category: "joy",
    template_category_name: "趣味模版",
    template_title: "百万蜂赏捞金",
    template_intro: "勤奋犒赏百万金",
    template_link: "E16",
    template_download_event_title: "蜂赏捞金 - 活动图包.zip",
    template_download_event_link:
      "https://bc9qk72ce.com/archives/蜂赏捞金 - 活动图包.zip",
    template_download_template_title: "蜂赏捞金 - 活动页全图包.zip",
    template_download_template_link:
      "https://bc9qk72ce.com/archives/蜂赏捞金 - 活动页全图包.zip",
    template_download_promotion_title: "蜂赏捞金 - 广宣素材.zip",
    template_download_promotion_link:
      "https://bc9qk72ce.com/archives/蜂赏捞金 - 广宣素材.zip",
    template_side_ad: ["01", "05", "08", "15"],
  },
  {
    template_id: 17,
    template_category: "festival",
    template_category_name: "节庆模版",
    template_title: "端午情至",
    template_intro: "威风龙舟粽百万",
    template_link: "E17",
    template_download_event_title: "端午情至 - 活动图包.zip",
    template_download_event_link:
      "https://bc9qk72ce.com/archives/端午情至 - 活动图包.zip",
    template_download_template_title: "端午情至 - 活动页全图包.zip",
    template_download_template_link:
      "https://bc9qk72ce.com/archives/端午情至 - 活动页全图包.zip",
    template_download_promotion_title: "端午情至 - 广宣素材.zip",
    template_download_promotion_link:
      "https://bc9qk72ce.com/archives/端午情至 - 广宣素材.zip",
    template_side_ad: ["18", "19", "16", "15"],
  },
  {
    template_id: 18,
    template_category: "festival",
    template_category_name: "节庆模版",
    template_title: "端午粽奖",
    template_intro: "万粽瞩目 包你中大奖",
    template_link: "E18",
    template_download_event_title: "端午粽奖 - 活动图包.zip",
    template_download_event_link:
      "https://bc9qk72ce.com/archives/端午粽奖 - 活动图包.zip",
    template_download_template_title: "端午粽奖 - 活动页全图包.zip",
    template_download_template_link:
      "https://bc9qk72ce.com/archives/端午粽奖 - 活动页全图包.zip",
    template_download_promotion_title: "端午粽奖 - 广宣素材.zip",
    template_download_promotion_link:
      "https://bc9qk72ce.com/archives/端午粽奖 - 广宣素材.zip",
    template_side_ad: ["17", "19", "16", "15"],
  },
  {
    template_id: 19,
    template_category: "joy",
    template_category_name: "趣味模版",
    template_title: "百万红包",
    template_intro: "百万红包大放送 天天加码送奖金",
    template_link: "E19",
    template_download_event_title: "百万红包 - 活动图包.zip",
    template_download_event_link:
      "https://bc9qk72ce.com/archives/百万红包 - 活动图包.zip",
    template_download_template_title: "百万红包 - 活动页全图包.zip",
    template_download_template_link:
      "https://bc9qk72ce.com/archives/百万红包 - 活动页全图包.zip",
    template_download_promotion_title: "百万红包 - 广宣素材.zip",
    template_download_promotion_link:
      "https://bc9qk72ce.com/archives/百万红包 - 广宣素材.zip",
    template_side_ad: ["16", "10", "12", "13"],
  },

  {
    template_id: 20,
    template_category: "joy",
    template_category_name: "趣味模版",
    template_title: "金球入门",
    template_intro: "金球入门 奖金入手",
    template_link: "E20",
    template_download_event_title: "金球入门 - 活动图包.zip",
    template_download_event_link:
      "https://bc9qk72ce.com/archives/金球入门 - 活动图包.zip",
    template_download_template_title: "金球入门 - 活动页全图包.zip",
    template_download_template_link:
      "https://bc9qk72ce.com/archives/金球入门 - 活动页全图包.zip",
    template_download_promotion_title: "金球入门 - 广宣素材.zip",
    template_download_promotion_link:
      "https://bc9qk72ce.com/archives/金球入门 - 广宣素材.zip",
    template_side_ad: ["15", "16", "13", "09"],
  },
  {
    template_id: 21,
    template_category: "joy",
    template_category_name: "趣味模版",
    template_title: "金喜蛋",
    template_intro: "蛋蛋有礼! 惊喜敲敲来",
    template_link: "E21",
    template_download_event_title: "金喜蛋 - 活动图包.zip",
    template_download_event_link:
      "https://bc9qk72ce.com/archives/金喜蛋 - 活动图包.zip",
    template_download_template_title: "金喜蛋 - 活动页全图包.zip",
    template_download_template_link:
      "https://bc9qk72ce.com/archives/金喜蛋 - 活动页全图包.zip",
    template_download_promotion_title: "金喜蛋 - 广宣素材.zip",
    template_download_promotion_link:
      "https://bc9qk72ce.com/archives/金喜蛋 - 广宣素材.zip",
    template_side_ad: ["22", "01", "09", "11"],
  },
  {
    template_id: 22,
    template_category: "festival",
    template_category_name: "节庆模版",
    template_title: "七夕情缘",
    template_intro: "七夕情缘金喜来 ",
    template_link: "E22",
    template_download_event_title: "七夕情缘 - 活动图包.zip",
    template_download_event_link:
      "https://bc9qk72ce.com/archives/七夕情缘 - 活动图包.zip",
    template_download_template_title: "七夕情缘 - 活动页全图包.zip",
    template_download_template_link:
      "https://bc9qk72ce.com/archives/七夕情缘 - 活动页全图包.zip",
    template_download_promotion_title: "七夕情缘 - 广宣素材.zip",
    template_download_promotion_link:
      "https://bc9qk72ce.com/archives/七夕情缘 - 广宣素材.zip",
    template_side_ad: ["06", "08", "13", "16"],
  },
  {
    template_id: 23,
    template_category: "fishing",
    template_category_name: "捕鱼模版",
    template_title: "百万捕鱼",
    template_intro: "百万奖潮来袭 喜迎财宝一炮爆富",
    template_link: "E23",
    template_download_event_title: "百万捕鱼 - 活动图包.zip",
    template_download_event_link:
      "https://bc9qk72ce.com/archives/百万捕鱼 - 活动图包.zip",
    template_download_template_title: "百万捕鱼 - 活动页全图包.zip",
    template_download_template_link:
      "https://bc9qk72ce.com/archives/百万捕鱼 - 活动页全图包.zip",
    template_download_promotion_title: "百万捕鱼 - 广宣素材.zip",
    template_download_promotion_link:
      "https://bc9qk72ce.com/archives/百万捕鱼 - 广宣素材.zip",
    template_side_ad: ["21", "16", "01", "10"],
  },

  {
    template_id: 26,
    template_category: "slot",
    template_category_name: "电子模版",
    template_title: "电子玛莉",
    template_intro: "电子玛莉 百万彩池赠豪礼",
    template_link: "E26",
    template_download_event_title: "电子玛莉 - 活动图包.zip",
    template_download_event_link:
      "https://bc9qk72ce.com/archives/电子玛莉 - 活动图包.zip",
    template_download_template_title: "电子玛莉 - 活动页全图包.zip",
    template_download_template_link:
      "https://bc9qk72ce.com/archives/电子玛莉 - 活动页全图包.zip",
    template_download_promotion_title: "电子玛莉 - 广宣素材.zip",
    template_download_promotion_link:
      "https://bc9qk72ce.com/archives/电子玛莉 - 广宣素材.zip",
    template_side_ad: ["27", "23", "21", "03"],
  },
  {
    template_id: 27,
    template_category: "live",
    template_category_name: "视讯模版",
    template_title: "视讯争霸战",
    template_intro: "视讯争霸战 千万大奖任你博",
    template_link: "E27",
    template_download_event_title: "视讯争霸战 - 活动图包.zip",
    template_download_event_link:
      "https://bc9qk72ce.com/archives/视讯争霸战 - 活动图包.zip",
    template_download_template_title: "视讯争霸战 - 活动页全图包.zip",
    template_download_template_link:
      "https://bc9qk72ce.com/archives/视讯争霸战 - 活动页全图包.zip",
    template_download_promotion_title: "视讯争霸战 - 广宣素材.zip",
    template_download_promotion_link:
      "https://bc9qk72ce.com/archives/视讯争霸战 - 广宣素材.zip",
    template_side_ad: ["26", "21", "11", "19"],
  },
  {
    template_id: 28,
    template_category: "festival",
    template_category_name: "节庆模版",
    template_title: "圣诞狂欢季",
    template_intro: "圣诞狂欢季 礼遇暖冬送大礼",
    template_link: "E28",
    template_download_event_title: "圣诞狂欢季 - 活动图包.zip",
    template_download_event_link:
      "https://bc9qk72ce.com/archives/圣诞狂欢季 - 活动图包.zip",
    template_download_template_title: "圣诞狂欢季 - 活动页全图包.zip",
    template_download_template_link:
      "https://bc9qk72ce.com/archives/圣诞狂欢季 - 活动页全图包.zip",
    template_download_promotion_title: "圣诞狂欢季 - 广宣素材.zip",
    template_download_promotion_link:
      "https://bc9qk72ce.com/archives/圣诞狂欢季 - 广宣素材.zip",
    template_side_ad: ["03", "02", "26", "27"],
  },
  {
    template_id: 29,
    template_category: "festival",
    template_category_name: "节庆模版",
    template_title: "新年火箭",
    template_intro: "百万豪礼一夜致富",
    template_link: "E29",
    template_download_event_title: "新年火箭 - 活动图包.zip",
    template_download_event_link:
      "https://bc9qk72ce.com/archives/新年火箭 - 活动图包.zip",
    template_download_template_title: "新年火箭 - 活动页全图包.zip",
    template_download_template_link:
      "https://bc9qk72ce.com/archives/新年火箭 - 活动页全图包.zip",
    template_download_promotion_title: "新年火箭 - 广宣素材.zip",
    template_download_promotion_link:
      "https://bc9qk72ce.com/archives/新年火箭 - 广宣素材.zip",
    template_side_ad: ["05", "21", "11", "19"],
  },
  {
    template_id: 30,
    template_category: "festival",
    template_category_name: "节庆模版",
    template_title: "端午射龙门",
    template_intro: "端午激战燃战火 百万挑战射龙门",
    template_link: "E30",
    template_download_event_title: "端午射龙门 - 活动图包.zip",
    template_download_event_link:
      "https://bc9qk72ce.com/archives/端午射龙门 - 活动图包.zip",
    template_download_template_title: "端午射龙门 - 活动页全图包.zip",
    template_download_template_link:
      "https://bc9qk72ce.com/archives/端午射龙门 - 活动页全图包.zip",
    template_download_promotion_title: "端午射龙门 - 广宣素材.zip",
    template_download_promotion_link:
      "https://bc9qk72ce.com/archives/端午射龙门 - 广宣素材.zip",
    template_side_ad: ["17", "18", "20", "22"],
  },
  {
    template_id: 32,
    template_category: "festival",
    template_category_name: "节庆模版",
    template_title: "七夕传情",
    template_intro: "七夕传情拼手速 翻牌百万迎财富",
    template_link: "E32",
    template_download_event_title: "七夕传情 - 重点图包.zip",
    template_download_event_link:
      "https://bc9qk72ce.com/archives/七夕传情 - 重点图包.zip",
    template_download_template_title: "七夕传情 - 活动页全图包.zip",
    template_download_template_link:
      "https://bc9qk72ce.com/archives/七夕传情 - 活动页全图包.zip",
    template_download_promotion_title: "七夕传情 - 广宣素材.zip",
    template_download_promotion_link:
      "https://bc9qk72ce.com/archives/七夕传情 - 广宣素材.zip",
    template_side_ad: ["22", "06", "13", "19"],
  },
  {
    template_id: 34,
    template_category: "festival",
    template_category_name: "节庆模版",
    template_title: "新春聚宝乐",
    template_intro: "福星聚宝乐 千万迎新春",
    template_link: "E34",
    template_download_event_title: "新春聚宝乐 - 重点图包.zip",
    template_download_event_link:
      "https://bc9qk72ce.com/archives/新春聚宝乐 - 重点图包.zip",
    template_download_template_title: "新春聚宝乐 - 活动页全图包.zip",
    template_download_template_link:
      "https://bc9qk72ce.com/archives/新春聚宝乐 - 活动页全图包.zip",
    template_download_promotion_title: "新春聚宝乐 - 广宣素材.zip",
    template_download_promotion_link:
      "https://bc9qk72ce.com/archives/新春聚宝乐 - 广宣素材.zip",
    template_side_ad: ["29", "19", "05", "01"],
  },

  {
    template_id: 35,
    template_category: "festival",
    template_category_name: "节庆模版",
    template_title: "端午乐其粽",
    template_intro: "乐在其粽 端午龙舟千万盛典",
    template_link: "E35",
    template_download_event_title: "端午乐其粽 - 重点图包.zip",
    template_download_event_link:
      "https://bc9qk72ce.com/archives/端午乐其粽 - 重点图包.zip",
    template_download_template_title: "端午乐其粽 - 活动页全图包.zip",
    template_download_template_link:
      "https://bc9qk72ce.com/archives/端午乐其粽 - 活动页全图包.zip",
    template_download_promotion_title: "端午乐其粽 - 广宣素材.zip",
    template_download_promotion_link:
      "https://bc9qk72ce.com/archives/端午乐其粽 - 广宣素材.zip",
    template_side_ad: ["30", "10", "13", "19"],
  },
  {
    template_id: 1,
    template_category: "festival",
    template_category_name: "节庆模版",
    template_title: "金喜紅包雨",
    template_intro: "金喜紅包雨 免费彩金抢不停",
    template_link: "E01",
    template_download_event_title: "红包雨 - 活动图包.zip",
    template_download_event_link:
      "https://bc9qk72ce.com/archives/红包雨 - 活动图包.zip",
    template_download_template_title: "红包雨 - 模版图包.zip",
    template_download_template_link:
      "https://bc9qk72ce.com/archives/红包雨 - 模版图包.zip",
    template_download_promotion_title: "红包雨 - 广宣素材.zip",
    template_download_promotion_link:
      "https://bc9qk72ce.com/archives/红包雨 - 广宣素材.zip",
    template_side_ad: ["05", "03", "02", "06"],
  },
  {
    template_id: 25,
    template_category: "festival",
    template_category_name: "节庆模版",
    template_title: "国庆盲盒",
    template_intro: "钜惠献礼迎国庆",
    template_link: "E25",
    template_download_event_title: "国庆盲盒 - 活动图包.zip",
    template_download_event_link:
      "https://bc9qk72ce.com/archives/国庆盲盒 - 活动图包.zip",
    template_download_template_title: "国庆盲盒 - 活动页全图包.zip",
    template_download_template_link:
      "https://bc9qk72ce.com/archives/国庆盲盒 - 活动页全图包.zip",
    template_download_promotion_title: "国庆盲盒 - 广宣素材.zip",
    template_download_promotion_link:
      "https://bc9qk72ce.com/archives/国庆盲盒 - 广宣素材.zip",
    template_side_ad: ["21", "02", "13", "09"],
  },
  {
    template_id: 33,
    template_category: "festival",
    template_category_name: "节庆模版",
    template_title: "烟火耀国庆",
    template_intro: "盛世烟火耀国庆  喜迎千万金",
    template_link: "E33",
    // template_tag_override 是臨時新增的強制tag，可以暫時將某個模版設為 hot 或 new，不用可拿掉
    template_tag_override: "hot", // "new" | "hot" | ""
    template_download_event_title: "烟火耀国庆 - 重点图包.zip",
    template_download_event_link:
      "https://bc9qk72ce.com/archives/烟火耀国庆 - 重点图包.zip",
    template_download_template_title: "烟火耀国庆 - 活动页全图包.zip",
    template_download_template_link:
      "https://bc9qk72ce.com/archives/烟火耀国庆 - 活动页全图包.zip",
    template_download_promotion_title: "烟火耀国庆 - 广宣素材.zip",
    template_download_promotion_link:
      "https://bc9qk72ce.com/archives/烟火耀国庆 - 广宣素材.zip",
    template_side_ad: ["24", "25", "15", "10"],
  },
  {
    template_id: 24,
    template_category: "festival",
    template_category_name: "节庆模版",
    template_title: "中秋博饼",
    template_intro: "中秋博饼 狀元金花拼百万",
    template_link: "E24",
    // template_tag_override 是臨時新增的強制tag，可以暫時將某個模版設為 hot 或 new，不用可拿掉
    template_tag_override: "hot", // "new" | "hot" | ""
    template_download_event_title: "中秋博饼 - 活动图包.zip",
    template_download_event_link:
      "https://bc9qk72ce.com/archives/中秋博饼 - 活动图包.zip",
    template_download_template_title: "中秋博饼 - 活动页全图包.zip",
    template_download_template_link:
      "https://bc9qk72ce.com/archives/中秋博饼 - 活动页全图包.zip",
    template_download_promotion_title: "中秋博饼 - 广宣素材.zip",
    template_download_promotion_link:
      "https://bc9qk72ce.com/archives/中秋博饼 - 广宣素材.zip",
    template_side_ad: ["19", "08", "13", "15"],
  },
  {
    template_id: 36,
    template_category: "festival",
    template_category_name: "节庆模版",
    template_title: "中秋叠叠乐",
    template_intro: "月满双节 层层礼遇千万相迎",
    template_link: "E36",
    template_download_event_title: "中秋叠叠乐 - 重点图包.zip",
    template_download_event_link:
      "https://bc9qk72ce.com/archives/中秋叠叠乐 - 重点图包.zip",
    template_download_template_title: "中秋叠叠乐 - 活动页全图包.zip",
    template_download_template_link:
      "https://bc9qk72ce.com/archives/中秋叠叠乐 - 活动页全图包.zip",
    template_download_promotion_title: "中秋叠叠乐 - 广宣素材.zip",
    template_download_promotion_link:
      "https://bc9qk72ce.com/archives/中秋叠叠乐 - 广宣素材.zip",
    template_side_ad: ["33", "25", "24", "01"],
  },
];

// 導出自動添加組件的完整資料
const TemplateItemsData = addTemplateComponents(BaseTemplateItemsData);

export default TemplateItemsData;
